import React, { useState, useEffect } from "react";
import { Form, Button, Spinner, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileQuestion, Type, ListChecks, CheckSquare, AlignLeft, Code,
  ImageIcon, Lightbulb, Plus, X,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, BackButton } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./QuizBank.scss";

const QUESTION_TYPES = [
  { value: "single_choice", label: "Single Choice", icon: CheckSquare, description: "One correct answer from multiple options" },
  { value: "multi_select", label: "Multi Select", icon: ListChecks, description: "Multiple correct answers" },
  { value: "true_false", label: "True / False", icon: Type, description: "True or false question" },
  { value: "text_answer", label: "Text Answer", icon: AlignLeft, description: "Student types the answer" },
  { value: "fill_blank", label: "Fill in Blank", icon: Type, description: "Fill in the missing word" },
  { value: "image_mcq", label: "Image MCQ", icon: ImageIcon, description: "Image-based multiple choice" },
  { value: "code_conceptual", label: "Code Conceptual", icon: Code, description: "Code analysis / predict output" },
];

const emptyOption = () => ({ text: "", imageUrl: "", isCorrect: false, _id: Date.now().toString() + Math.random() });

const emptyForm = {
  category: "",
  subcategory: "",
  tags: [],
  questionText: "",
  questionType: "single_choice",
  imageUrl: "",
  options: [emptyOption(), emptyOption(), emptyOption(), emptyOption()],
  acceptedAnswers: [""],
  codeSnippet: "",
  codeLanguage: "javascript",
  difficulty: "medium",
  marks: 1,
  negativeMarks: 0,
  explanation: "",
  hint: "",
  source: "",
  estimatedTime: 60,
};

const AddEditQuizQuestionPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Fetch categories
  useEffect(() => {
    apiClient.get("/quiz-categories?flat=true")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  // When category changes, compute subcategories
  useEffect(() => {
    if (form.category) {
      const subs = categories.filter(c => c.parentCategory?.toString() === form.category);
      setSubcategories(subs);
    } else {
      setSubcategories([]);
    }
  }, [form.category, categories]);

  // Load existing question for edit
  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      apiClient.get(`/quiz-questions/${id}`)
        .then(({ data }) => {
          const q = data.question;
          setForm({
            category: q.category?._id || q.category || "",
            subcategory: q.subcategory?._id || q.subcategory || "",
            tags: q.tags || [],
            questionText: q.questionText || "",
            questionType: q.questionType || "single_choice",
            imageUrl: q.imageUrl || "",
            options: q.options?.length > 0 ? q.options : [emptyOption(), emptyOption()],
            acceptedAnswers: q.acceptedAnswers?.length > 0 ? q.acceptedAnswers : [""],
            codeSnippet: q.codeSnippet || "",
            codeLanguage: q.codeLanguage || "javascript",
            difficulty: q.difficulty || "medium",
            marks: q.marks || 1,
            negativeMarks: q.negativeMarks || 0,
            explanation: q.explanation || "",
            hint: q.hint || "",
            source: q.source || "",
            estimatedTime: q.estimatedTime || 60,
          });
        })
        .catch(() => setToast({ show: true, message: "Failed to load question", type: "error" }))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // ─── Options Management ───────────────────────────────────────────
  const updateOption = (index, field, value) => {
    setForm(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = { ...newOptions[index], [field]: value };

      // For single choice / true_false / image_mcq / code_conceptual, ensure only one correct
      if (field === "isCorrect" && value === true) {
        const singleTypes = ["single_choice", "true_false", "image_mcq", "code_conceptual"];
        if (singleTypes.includes(prev.questionType)) {
          newOptions.forEach((o, i) => {
            if (i !== index) newOptions[i] = { ...o, isCorrect: false };
          });
        }
      }
      return { ...prev, options: newOptions };
    });
  };

  const addOption = () => {
    setForm(prev => ({ ...prev, options: [...prev.options, emptyOption()] }));
  };

  const removeOption = (index) => {
    if (form.options.length <= 2) return;
    setForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  // ─── Accepted Answers Management ─────────────────────────────────
  const updateAcceptedAnswer = (index, value) => {
    setForm(prev => {
      const newAnswers = [...prev.acceptedAnswers];
      newAnswers[index] = value;
      return { ...prev, acceptedAnswers: newAnswers };
    });
  };

  const addAcceptedAnswer = () => {
    setForm(prev => ({ ...prev, acceptedAnswers: [...prev.acceptedAnswers, ""] }));
  };

  const removeAcceptedAnswer = (index) => {
    if (form.acceptedAnswers.length <= 1) return;
    setForm(prev => ({ ...prev, acceptedAnswers: prev.acceptedAnswers.filter((_, i) => i !== index) }));
  };

  // ─── Tags ─────────────────────────────────────────────────────────
  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !form.tags.includes(trimmed)) {
      updateField("tags", [...form.tags, trimmed]);
    }
    setTagInput("");
  };

  const removeTag = (tag) => {
    updateField("tags", form.tags.filter(t => t !== tag));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === "Backspace" && !tagInput && form.tags.length > 0) {
      removeTag(form.tags.at(-1));
    }
  };

  // ─── Question Type Change ─────────────────────────────────────────
  const handleTypeChange = (newType) => {
    setForm(prev => {
      const updated = { ...prev, questionType: newType };
      if (newType === "true_false") {
        updated.options = [
          { text: "True", imageUrl: "", isCorrect: true, _id: "tf-true" },
          { text: "False", imageUrl: "", isCorrect: false, _id: "tf-false" },
        ];
      } else if (["single_choice", "multi_select", "image_mcq", "code_conceptual"].includes(newType)) {
        if (prev.options.length < 2) {
          updated.options = [emptyOption(), emptyOption(), emptyOption(), emptyOption()];
        }
      }
      return updated;
    });
  };

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.questionText.trim()) {
      setToast({ show: true, message: "Question text is required", type: "error" });
      return;
    }
    if (!form.category) {
      setToast({ show: true, message: "Please select a category", type: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        subcategory: form.subcategory || undefined,
        acceptedAnswers: form.acceptedAnswers.filter(a => a.trim()),
        tags: form.tags,
      };

      if (isEdit) {
        await apiClient.put(`/quiz-questions/${id}`, payload);
        setToast({ show: true, message: "Question updated successfully", type: "success" });
      } else {
        await apiClient.post("/quiz-questions", payload);
        setToast({ show: true, message: "Question created successfully", type: "success" });
      }
      setTimeout(() => navigate("/quiz-bank"), 500);
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to save question",
        type: "error",
      });
    }
    setSaving(false);
  };

  const isChoiceType = ["single_choice", "multi_select", "true_false", "image_mcq", "code_conceptual"].includes(form.questionType);
  const isTextType = ["text_answer", "fill_blank"].includes(form.questionType);
  const isCodeType = form.questionType === "code_conceptual";
  const isImageType = form.questionType === "image_mcq";

  // Top-level categories only
  const topCategories = categories.filter(c => !c.parentCategory);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="add-edit-quiz-question-page">
      <BackButton />
      <PageHeader
        icon={FileQuestion}
        title={isEdit ? "Edit Question" : "Add Question"}
        description={isEdit ? "Update question details" : "Create a new question for your bank"}
      />

      <Form onSubmit={handleSubmit}>
        {/* ─── Question Type Selection ────────────────────────────── */}
        <div className="form-section">
          <div className="form-section__title">
            <ListChecks /> Question Type
          </div>
          <Row className="g-2">
            {QUESTION_TYPES.map((qt) => {
              const Icon = qt.icon;
              return (
                <Col xs={6} md={4} lg={3} key={qt.value}>
                  <button
                    type="button"
                    className={`p-2 border rounded text-center cursor-pointer w-100 bg-transparent ${
                      form.questionType === qt.value
                        ? "border-primary bg-primary bg-opacity-10"
                        : ""
                    }`}
                    onClick={() => handleTypeChange(qt.value)}
                  >
                    <Icon size={20} className="mb-1" />
                    <div className="small fw-semibold">{qt.label}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>{qt.description}</div>
                  </button>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* ─── Question Content ───────────────────────────────────── */}
        <div className="form-section">
          <div className="form-section__title">
            <Type /> Question Content
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Question Text *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.questionText}
              onChange={(e) => updateField("questionText", e.target.value)}
              placeholder="Enter your question here..."
              required
            />
          </Form.Group>

          {(isImageType || form.questionType === "image_mcq") && (
            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                value={form.imageUrl}
                onChange={(e) => updateField("imageUrl", e.target.value)}
                placeholder="Paste image URL (S3 link or external URL)"
              />
            </Form.Group>
          )}

          {isCodeType && (
            <>
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Programming Language</Form.Label>
                    <Form.Select
                      value={form.codeLanguage}
                      onChange={(e) => updateField("codeLanguage", e.target.value)}
                    >
                      <option value="javascript">JavaScript</option>
                      <option value="python">Python</option>
                      <option value="java">Java</option>
                      <option value="c">C</option>
                      <option value="cpp">C++</option>
                      <option value="sql">SQL</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Code Snippet</Form.Label>
                <div className="code-snippet-editor">
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={form.codeSnippet}
                    onChange={(e) => updateField("codeSnippet", e.target.value)}
                    placeholder="Paste the code snippet students need to analyze..."
                    className="font-monospace"
                  />
                </div>
              </Form.Group>
            </>
          )}
        </div>

        {/* ─── Answer Options (MCQ types) ─────────────────────────── */}
        {isChoiceType && (
          <div className="form-section">
            <div className="form-section__title">
              <CheckSquare /> Answer Options
              <span className="text-muted small ms-2">
                {form.questionType === "multi_select"
                  ? "(select all correct answers)"
                  : "(select the correct answer)"}
              </span>
            </div>

            <div className="options-list">
              {form.options.map((opt, idx) => (
                <div
                  key={opt._id || idx}
                  className={`option-row ${opt.isCorrect ? "option-row--correct" : ""}`}
                >
                  <Form.Check
                    type={form.questionType === "multi_select" ? "checkbox" : "radio"}
                    name="correctOption"
                    checked={opt.isCorrect}
                    onChange={(e) => updateOption(idx, "isCorrect", e.target.checked)}
                    className="me-1"
                  />
                  <Form.Control
                    className="option-row__input"
                    size="sm"
                    value={opt.text}
                    onChange={(e) => updateOption(idx, "text", e.target.value)}
                    placeholder={`Option ${String.fromCodePoint(65 + idx)}`}
                    disabled={form.questionType === "true_false"}
                  />
                  {form.questionType !== "true_false" && form.options.length > 2 && (
                    <button
                      type="button"
                      className="option-row__remove btn btn-link p-0"
                      onClick={() => removeOption(idx)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {form.questionType !== "true_false" && (
              <Button
                variant="link"
                size="sm"
                className="mt-2 p-0"
                onClick={addOption}
              >
                <Plus size={14} className="me-1" /> Add Option
              </Button>
            )}
          </div>
        )}

        {/* ─── Accepted Answers (Text types) ──────────────────────── */}
        {isTextType && (
          <div className="form-section">
            <div className="form-section__title">
              <AlignLeft /> Accepted Answers
              <span className="text-muted small ms-2">(case-insensitive match)</span>
            </div>
            <div className="accepted-answers-list">
              {form.acceptedAnswers.map((ans, idx) => (
                <div key={`ans-${ans || idx}`} className="accepted-answer-row">
                  <Form.Control
                    size="sm"
                    value={ans}
                    onChange={(e) => updateAcceptedAnswer(idx, e.target.value)}
                    placeholder={`Accepted answer ${idx + 1}`}
                  />
                  {form.acceptedAnswers.length > 1 && (
                    <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => removeAcceptedAnswer(idx)}>
                      <X size={16} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="link" size="sm" className="mt-2 p-0" onClick={addAcceptedAnswer}>
              <Plus size={14} className="me-1" /> Add Variation
            </Button>
          </div>
        )}

        {/* ─── Classification & Scoring ───────────────────────────── */}
        <div className="form-section">
          <div className="form-section__title">
            <Lightbulb /> Classification & Scoring
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Category *</Form.Label>
                <Form.Select
                  value={form.category}
                  onChange={(e) => {
                    updateField("category", e.target.value);
                    updateField("subcategory", "");
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {topCategories.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Subcategory</Form.Label>
                <Form.Select
                  value={form.subcategory}
                  onChange={(e) => updateField("subcategory", e.target.value)}
                  disabled={subcategories.length === 0}
                >
                  <option value="">None</option>
                  {subcategories.map((s) => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Difficulty *</Form.Label>
                <Form.Select
                  value={form.difficulty}
                  onChange={(e) => updateField("difficulty", e.target.value)}
                  required
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Marks</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.marks}
                  onChange={(e) => updateField("marks", Number.parseFloat(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Negative Marks</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step={0.25}
                  value={form.negativeMarks}
                  onChange={(e) => updateField("negativeMarks", Number.parseFloat(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Estimated Time (seconds)</Form.Label>
                <Form.Control
                  type="number"
                  min={5}
                  value={form.estimatedTime}
                  onChange={(e) => updateField("estimatedTime", Number.parseInt(e.target.value, 10) || 60)}
                />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Source</Form.Label>
                <Form.Control
                  value={form.source}
                  onChange={(e) => updateField("source", e.target.value)}
                  placeholder="e.g., GRE 2024, Aptitude Book Chapter 3"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Tags */}
          <div className="mt-3">
            <Form.Label>Tags <span className="text-muted small">(press Enter to add)</span></Form.Label>
            <div className="tags-input">
              {form.tags.map((tag) => (
                <span className="tags-input__tag" key={tag}>
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => tagInput && addTag(tagInput)}
                placeholder={form.tags.length === 0 ? "Placement, IELTS, Campus..." : ""}
              />
            </div>
          </div>
        </div>

        {/* ─── Explanation & Hint ─────────────────────────────────── */}
        <div className="form-section">
          <div className="form-section__title">
            <Lightbulb /> Explanation & Hint
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Explanation <span className="text-muted small">(shown after attempt)</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.explanation}
              onChange={(e) => updateField("explanation", e.target.value)}
              placeholder="Explain why the correct answer is right..."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Hint <span className="text-muted small">(optional)</span></Form.Label>
            <Form.Control
              value={form.hint}
              onChange={(e) => updateField("hint", e.target.value)}
              placeholder="A helpful hint for students"
            />
          </Form.Group>
        </div>

        {/* ─── Actions ────────────────────────────────────────────── */}
        <div className="form-actions">
          <Button variant="light" onClick={() => navigate("/quiz-bank")}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving && <Spinner size="sm" animation="border" />}
            {!saving && (isEdit ? "Save Changes" : "Create Question")}
          </Button>
        </div>
      </Form>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default AddEditQuizQuestionPage;
